import { Extension } from '@tiptap/core'
import { DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2, fontList } from '../fonts'

export type FontFamilyOptions = {
  types: string[]
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontFamily: {
      /**
       * Set the font family
       */
      setFontFamily: (fontFamily: string) => ReturnType
      /**
       * Unset the font family
       */
      unsetFontFamily: () => ReturnType
    }
  }
}

export const FontFamilyV2 = Extension.create<FontFamilyOptions>({
  name: 'fontFamilyV2',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: element => {
              const fontFamily = element.style.fontFamily
              if (!fontFamily) return null

              // Split the font family string by commas and handle each font separately
              const fonts = fontFamily.match(/("[^"]*"|'[^']*'|[^,]+)/g) || []

              // Clean and normalize each font name
              const normalizedFonts = fonts.map(font => {
                // Trim whitespace
                font = font.trim()
                // Remove any existing quotes (single or double)
                font = font.replace(/^["']|["']$/g, '')
                return font
              })

              // Find the first valid font from the list, or default to Open Sans
              const validFont = normalizedFonts.find(font =>
                fontList.includes(font)
              )

              return validFont || DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2
            },
            renderHTML: attributes => {
              if (!attributes.fontFamily) {
                return {}
              }

              // This is different from a simple split(',') because it properly handles quoted font names and preserves the quotes where they are needed
              const formattedFonts =
                attributes.fontFamily.match(/('[^']*'|"[^"]*"|[^,]+)/g) || []

              // Clean and format each font name
              const formatted = formattedFonts.map((font: string) => {
                // Trim whitespace
                font = font.trim()
                // Remove existing quotes
                font = font.replace(/^['"]|['"]$/g, '')

                return font
              })

              // Validate font against the fontList
              const validFont = formatted.find(font => fontList.includes(font))
              const finalFont =
                validFont || DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2

              return {
                style: `font-family: ${finalFont}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontFamily:
        fontFamily =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontFamily }).run()
        },
      unsetFontFamily:
        () =>
        ({ chain }) => {
          return chain()
            .setMark('textStyle', { fontFamily: null })
            .removeEmptyTextStyle()
            .run()
        },
    }
  },
})
